import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Bot } from "interfaces/db";

// State interface with proper typing
export interface PracticeBotState {
  sessionBots: Record<string, {
    bot: Bot | null;
    isConstructing: boolean;
    isLoading: boolean;
    isLoaded: boolean;
    error: string | null;
  }>;
}

// Initial state
const initialState: PracticeBotState = {
  sessionBots: {},
};

export const practiceBotSlice = createSlice({
  name: 'practiceBot',
  initialState,
  reducers: {
    setSessionBotLoading: (state, action: PayloadAction<{
      sessionId: string;
      isLoading: boolean;
    }>) => {
      const { sessionId, isLoading } = action.payload;
      state.sessionBots[sessionId] = {
        ...(state.sessionBots[sessionId] || {
          bot: null,
          isLoaded: false,
          isConstructing: false,
          error: null,
        }),
        isLoading,
      };
    },
    setSessionBotLoaded: (state, action: PayloadAction<{
      sessionId: string;
      isLoaded: boolean;
    }>) => {
      const { sessionId, isLoaded } = action.payload;
      state.sessionBots[sessionId] = {
        ...(state.sessionBots[sessionId] || {
          bot: null,
          isLoading: false,
          isConstructing: false,
          error: null,
        }),
        isLoaded,
      };
    },
    setSessionBot: (state, action: PayloadAction<{
      sessionId: string;
      bot: Bot | null;
    }>) => {
      const { sessionId, bot } = action.payload;
      state.sessionBots[sessionId] = {
        ...(state.sessionBots[sessionId] || {
          isLoading: false,
          error: null,
        }),
        bot,
        isLoaded: true,
      };
    },
    
    setSessionBotError: (state, action: PayloadAction<{
      sessionId: string;
      error: string | null;
    }>) => {
      const { sessionId, error } = action.payload;
      state.sessionBots[sessionId] = {
        ...(state.sessionBots[sessionId] || {
          bot: null,
          isLoaded: true,
          isLoading: false,
        }),
        error,
      };
    },
    
    // Add a new bot to the state
    addPracticeBot: (state, action: PayloadAction<{
      sessionId: string;
      bot: Bot;
    }>) => {
      const { sessionId, bot } = action.payload;
      state.sessionBots[sessionId] = {
        bot,
        isLoading: false,
        isLoaded: true,
        isConstructing: false,
        error: null,
      };
    },
    
    // Update an existing bot
    updatePracticeBot: (state, action: PayloadAction<{
      sessionId: string;
      botUpdate: Partial<Bot>;
    }>) => {
      const { sessionId, botUpdate } = action.payload;
      const existingBot = state.sessionBots[sessionId]?.bot;
      
      if (existingBot) {
        state.sessionBots[sessionId].bot = {
          ...existingBot,
          ...botUpdate,
        };
      }
    },

    setIsConstructing: (state, action: PayloadAction<{
      sessionId: string;
      isConstructing: boolean;
    }>) => {
      const { sessionId, isConstructing } = action.payload;
      state.sessionBots[sessionId].isConstructing = isConstructing;
    },
    
    // Clear all practice bot data (useful for logout/reset)
    clearPracticeBots: (state) => {
      state.sessionBots = {};
    },
  },
});

export const {
  setSessionBotLoading,
  setSessionBotLoaded,
  setSessionBot,
  setSessionBotError,
  addPracticeBot,
  updatePracticeBot,
  clearPracticeBots,
  setIsConstructing,
} = practiceBotSlice.actions;

export default practiceBotSlice.reducer; 